import { motion } from "framer-motion";
import { GraduationCap, BookOpen, Users, Baby, User, Briefcase, Check } from "lucide-react";

const levels = [
  {
    icon: Baby,
    name: "Primaire",
    range: "CP - CM2",
    color: "from-green-400 to-emerald-500"
  },
  {
    icon: BookOpen,
    name: "Collège",
    range: "6ème - 3ème",
    color: "from-blue-400 to-blue-600"
  },
  {
    icon: GraduationCap,
    name: "Lycée",
    range: "2nde - Terminale",
    color: "from-purple-400 to-purple-600"
  },
  {
    icon: Briefcase,
    name: "Adultes",
    range: "Formation continue",
    color: "from-orange-400 to-orange-600"
  }
];

const subjects = [
  { name: "Mathématiques", forAll: true },
  { name: "Français", forAll: true },
  { name: "Anglais", forAll: true },
  { name: "Histoire-Géographie", forAll: true },
  { name: "Physique-Chimie", forAll: true },
  { name: "SVT", forAll: true },
  { name: "Espagnol", forAll: true },
  { name: "Allemand", forAll: true },
  { name: "Philosophie", forAll: true },
  { name: "SES", forAll: true }
];

const adultCourses = [
  { name: "Anglais professionnel", description: "Conversation, business English" },
  { name: "FLE", description: "Français Langue Étrangère" }
];

export const LevelsSubjects = () => {
  return (
    <section
      id="niveaux"
      data-testid="levels-section"
      className="py-14 md:py-28 relative bg-gradient-to-b from-blue-50 to-white overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-blue-200 rounded-full blur-3xl opacity-30 hidden sm:block" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-30 hidden sm:block" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <span className="inline-block text-blue-600 font-semibold mb-4 tracking-wider uppercase text-sm bg-blue-100 px-4 py-2 rounded-full">
            Niveaux & Matières
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold text-blue-900 mb-4 md:mb-6">
            Du CP aux adultes, <span className="text-orange-500">toutes matières</span>
          </h2>
          <p className="text-base md:text-lg text-blue-800/70 max-w-2xl mx-auto">
            Un accompagnement adapté à chaque niveau et à chaque besoin, 
            de l'école primaire à la formation adulte.
          </p>
        </motion.div>

        {/* Levels Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16"
        >
          {levels.map((level, index) => (
            <motion.div
              key={level.name}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all text-center overflow-hidden"
            >
              {/* Gradient top bar */}
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${level.color}`} />
              
              <div className={`w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-r ${level.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                <level.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-blue-900 text-lg mb-1">{level.name}</h3>
              <p className="text-blue-600/70 text-sm">{level.range}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Subjects - Simple list */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl p-5 sm:p-8 md:p-10 shadow-lg border border-gray-100 max-w-4xl mx-auto"
        >
          <div className="text-center mb-6 md:mb-8">
            <h3 className="font-bold text-blue-900 text-xl sm:text-2xl mb-2">Toutes les matières enseignées</h3>
            <p className="text-blue-600/70 text-sm sm:text-base">Du CP à la Terminale + Formations adultes</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {[...subjects, { name: "Anglais adultes", forAll: false }, { name: "FLE", forAll: false }].map((subject, index) => (
              <motion.div
                key={subject.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
                className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all hover:scale-105 ${
                  subject.forAll 
                    ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                    : 'bg-orange-50 text-orange-700 border border-orange-200'
                }`}
              >
                {subject.name}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 shadow-lg"
          >
            <span>Trouver mon cours</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};
